import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Calendar, User, Clock, IndianRupee } from 'lucide-react';
import api from '../api/axiosInstance';

const SiteDetailedAudit = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({ project: {}, logs: [] });
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

    useEffect(() => { fetchDetails(); }, [projectId, month]);

    const fetchDetails = async () => {
        const res = await api.get(`/get_site_detailed_audit.php?project_id=${projectId}&month=${month}`);
        setData(res.data);
    };

    // NEW: Helper to get days in the currently selected month
    const getDaysInMonth = (monthStr) => {
        const [year, month] = monthStr.split('-');
        return new Date(year, month, 0).getDate();
    };

    const calculateDailyCost = (log) => {
        const rate = parseFloat(log.applied_rate);
        const multiplier = parseFloat(log.multiplier);

        if (log.pay_type === 'Hourly') {
            // 1. Hourly Logic
            const start = new Date(`2000-01-01 ${log.in_time}`);
            let end = new Date(`2000-01-01 ${log.out_time}`);
            if (end <= start) end.setDate(end.getDate() + 1);
            const hours = (end - start) / 3600000;
            return hours * rate;
        } else if (log.pay_type === 'Monthly') {
            // 2. Monthly Logic: (Rate / Days in Month) * Multiplier
            const daysInMonth = getDaysInMonth(month);
            return (rate / daysInMonth) * multiplier;
        } else {
            // 3. Daily Logic
            return rate * multiplier;
        }
    };

    return (
        <div className="p-8 space-y-8 bg-slate-50 min-h-screen">
            <div className="flex items-center justify-between no-print">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-black text-[10px] uppercase text-slate-400 hover:text-slate-900 transition-all">
                    <ArrowLeft size={16} /> Back to Analysis
                </button>
                <button onClick={() => window.print()} className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2">
                    <Printer size={16} /> Print Audit Report
                </button>
            </div>

            <div className="bg-white rounded-[48px] p-10 shadow-sm border border-slate-100 print:shadow-none print:border-none print:p-0">
                <div className="flex justify-between items-end mb-12">
                    <div>
                        <h1 className="text-4xl font-black italic uppercase tracking-tighter text-slate-900">{data.project?.project_name}</h1>
                        <p className="text-slate-400 font-bold text-sm uppercase">Site Labor Audit • {new Date(month + "-01").toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                    </div>
                    <div className="text-right no-print">
                        <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Change Month</p>
                        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-slate-50 border-none rounded-xl p-3 font-black text-xs uppercase outline-none" />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                <th className="pb-6">Date</th>
                                <th className="pb-6">Worker</th>
                                <th className="pb-6">Work Details</th>
                                <th className="pb-6">Rate Applied</th>
                                <th className="pb-6 text-right">Daily Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {data.logs.map((log, i) => {
                                const cost = calculateDailyCost(log);
                                return (
                                    <tr key={i} className="group hover:bg-slate-50/50 transition-all">
                                        <td className="py-6 font-black text-slate-900 italic text-sm">{log.date}</td>
                                        <td className="py-6">
                                            <p className="font-black text-slate-900 uppercase text-xs">{log.worker_name}</p>
                                            <span className="text-[8px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded uppercase font-bold tracking-tighter">{log.pay_type} Basis</span>
                                        </td>
                                        <td className="py-6 font-bold text-slate-600 text-xs uppercase">
                                            {log.pay_type === 'Hourly'
                                                ? <span className="flex items-center gap-1"><Clock size={12} /> {log.in_time.substring(0, 5)} - {log.out_time.substring(0, 5)}</span>
                                                : `${log.multiplier} Shift Count`
                                            }
                                        </td>
                                        <td className="py-6 font-bold text-slate-400 text-[10px]">
                                            ₹{parseFloat(log.applied_rate).toLocaleString()} / {log.pay_type === 'Monthly' ? 'Month' : log.pay_type === 'Hourly' ? 'Hr' : 'Day'}
                                        </td>
                                        <td className="py-6 text-right font-black text-indigo-600 italic">
                                            ₹{cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-slate-900 text-white">
                                <td colSpan="4" className="p-8 rounded-l-[32px] font-black uppercase text-[10px] italic tracking-[0.2em]">Total Project Labor Investment</td>
                                <td className="p-8 rounded-r-[32px] text-right font-black text-2xl italic">
                                    ₹{data.logs.reduce((sum, log) => sum + calculateDailyCost(log), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SiteDetailedAudit;